<script setup lang="ts">
import { ref, watch } from "vue";

import { getDownloadUrl } from "@/api/client";

const props = withDefaults(
  defineProps<{ fileIds: string[]; removable?: boolean }>(),
  { removable: false },
);
defineEmits<{ remove: [fileId: string] }>();

// fileId -> resolved short-lived presigned URL (absent until loaded/failed).
const urls = ref<Record<string, string>>({});
const failed = ref<Set<string>>(new Set());

watch(
  () => props.fileIds,
  async (ids) => {
    for (const fileId of ids ?? []) {
      if (urls.value[fileId] || failed.value.has(fileId)) continue;
      try {
        urls.value = { ...urls.value, [fileId]: await getDownloadUrl(fileId) };
      } catch {
        failed.value = new Set(failed.value).add(fileId);
      }
    }
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
    <div
      v-for="fileId in fileIds"
      :key="fileId"
      class="relative"
    >
      <button
        v-if="removable"
        type="button"
        title="Remove photo"
        class="absolute right-1 top-1 z-[1] grid h-6 w-6 place-items-center rounded-full bg-neutral-900/70 text-xs text-white transition hover:bg-semantic-danger"
        @click.prevent="$emit('remove', fileId)"
      >
        ✕
      </button>
      <a
        v-if="urls[fileId]"
        :href="urls[fileId]"
        target="_blank"
        rel="noopener"
        title="Open full size"
        class="block overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50"
      >
        <img
          :src="urls[fileId]"
          alt="site photo"
          loading="lazy"
          class="aspect-square w-full object-cover transition hover:opacity-90"
        >
      </a>
      <div
        v-else-if="failed.has(fileId)"
        class="flex aspect-square items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 text-xs text-neutral-400"
      >
        Unavailable
      </div>
      <div
        v-else
        class="aspect-square animate-pulse rounded-lg border border-neutral-200 bg-neutral-100"
      />
    </div>
  </div>
</template>
